import { InlineAlertType,InlineAlertWidget } from '@tableau/widgets';
import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { BrowserTitleService } from '~commonInfra/services/BrowserTitleService';
import { PredictiveModelDefinition } from '~src/predictiveModel/contentDefinition/PredictiveModelDefinition';
import { ProjectDefinition } from '~src/projects/components/contentDefinitions/ProjectDefinition';
import { populateContentBreadcrumbs } from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbHelpers';
import { ConnectedExplorer } from '~src/spacesPlatform/components/Explorer/ConnectedExplorer';
import { CreateContentButtonLocations, CreateContentButtonOwnProps } from '~src/spacesPlatform/components/Explorer/CreateContent/CreateContentButton';
import { ExplorerStyles } from '~src/spacesPlatform/components/Explorer/ExplorerStyles';
import { ContentSpecificControls, ExplorerToolbarTestIds } from '~src/spacesPlatform/components/Explorer/ExplorerToolbar';
import { FilterByTypeDropdown } from '~src/spacesPlatform/components/Explorer/FilterByTypeDropdown';
import { ExplorerEmptyStateProps } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerEmpyState';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { createContentButton } from '~src/spacesPlatform/components/spaceComponents/SubspaceHelpers';
import { getPersonalSpaceContentInOrder } from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { NullProjectIdForNonProjects,SpaceType } from '~src/spacesPlatform/state/SpaceInfo';
import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';

import { Messages } from '../../../codegen/Localize';
import { ExplorableItemType } from '../../../spacesPlatform/models/ContentType';
import { PersonalSpaceInfo } from '../../restApi/PersonalSpace';

const testIdPrefix = 'personal-space';
const EMPTY_STATE_TEST_ID = `${testIdPrefix}-empty-state`;

export interface PersonalSpaceOwnProps {
  readonly urls: SpacesSiteUrls;
}

export interface PersonalSpaceStateProps {
  readonly contentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly personalSpace: PersonalSpaceInfo;
}

export interface PersonalSpaceDispatchProps {
  readonly requestSpaceContents: () => void;
  readonly filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void;
  readonly clearContentCounts: (spaceType: SpaceType) => void;
  readonly getPersonalSpace: () => void;
  readonly createNewWorkbook: (projectId?: string) => void;
}

export type PersonalSpaceProps = PersonalSpaceStateProps
  & PersonalSpaceDispatchProps
  & PersonalSpaceOwnProps;

export class PersonalSpace extends React.PureComponent<PersonalSpaceProps> {

  public componentDidMount() {
    BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: Messages.personalSpaceTitle() });
    this.props.requestSpaceContents();
    this.props.getPersonalSpace();
    // This is needed so the breadcrumb if set is cleared when personal page is loaded.
    populateContentBreadcrumbs([], this.props.urls);
  }

  public render(): JSX.Element {
    const createContentButtonProps: CreateContentButtonOwnProps = {
      location: CreateContentButtonLocations.PersonalSpace,
      projectId: NullProjectIdForNonProjects,
      spaceType: 'personal'
    };
    const toolbarContentSpecificControlsProps: ContentSpecificControls = { beginning: this.props.personalSpace.readOnly ? undefined : createContentButton(createContentButtonProps)};
    return (
      <div className={SpaceContainerStyles.SpaceContainer}>
        <div className={SpaceContainerStyles.MastheadArea}>
          <div className={ExplorerStyles.SpaceHeaderContent}>
            <div className={ExplorerStyles.SpaceHeaderText}>{Messages.personalSpaceTitle()}</div>
          </div>
          <div style={SpaceContainerStyles.MastheadSubheadingArea}>
            {Messages.personalSpaceMastheadSubheading()}
          </div>
          {this.personalSpaceWarning(this.props.personalSpace.readOnly)}
        </div>
        <div className={SpaceContainerStyles.SubplaceArea}>
          <ConnectedExplorer
            supportedDisplayModes={ProjectDefinition.supportedDisplayModes}
            emptyStateProps={this.emptyStateProps()}
            toolbarProps={{
              supportFilterPanel: true,
              toolbarContentFilterControl: FeatureFlags.prometheusPredictiveModelEnabled ? this.contentFilterControl(): undefined,
              toolbarVisibility: 'enabled',
              hideContentSpecificControlsInEmptyState: true,
              toolbarContentSpecificControls: toolbarContentSpecificControlsProps,
            }}
          />
        </div>
      </div>
    );
  }

  private contentFilterControl(): JSX.Element {
    return (
      <React.Fragment>
        <span id={ExplorerToolbarTestIds.FilterByTypeDropdownButtonLabel} className={ExplorerStyles.ToolbarDropdownLabel}>
           {Messages.filterByTypeDropdown_label()}
        </span>
        <FilterByTypeDropdown
          filterSelected={this.props.filterSelected}
          contentFilter={this.props.contentTypeFilterValues}
          defaultContentTypeFilterValues={this.props.defaultContentTypeFilterValues}
          showWorkbooksAsViews={true}
          clearContentCounts={this.props.clearContentCounts}
          spaceType={'personal'}
          contentItemDefinitions={FeatureFlags.prometheusPredictiveModelEnabled ?
            [...getPersonalSpaceContentInOrder(), PredictiveModelDefinition] :
            getPersonalSpaceContentInOrder()}
        />
      </React.Fragment>
    );
  }

  private personalSpaceWarning(readonly: boolean) {
    if (!readonly) {
      return null;
    }
    return (
      <InlineAlertWidget
        testId={'personalspace-warning'}
        type={InlineAlertType.Warning}
        style={{marginTop: '12px'}}
      >
        {Messages.personalSpaceReadOnlyWarning()}
      </InlineAlertWidget>
    );
  }

  private emptyStateProps(): ExplorerEmptyStateProps {
    return {
      primaryMessage: Messages.personalSpaceEmptyStatePrimary(),
      secondaryMessage: Messages.personalSpaceEmptyStateSecondary(),
      dataTestId: EMPTY_STATE_TEST_ID,
      actionButtonMessage: this.props.personalSpace.readOnly ? undefined : Messages.personalSpaceEmptyStateActionButton(),
      onActionButton: () => this.props.createNewWorkbook(),
    };
  }
}
